import React from 'react';
import { Box, Paper } from '@mui/material';
import type { MCQuestion } from '../types.ts';
import RichText from '../../../components/RichText.tsx';
import { choicesAsFlatStrings, getTableChoiceData } from '../../../utils/questionChoices.ts';
import { McChoiceTableStudent } from '../../../components/McChoiceTableStudent.tsx';

type Props = {
  question: MCQuestion;
  selected: number | undefined;
  onSelect: (idx: number) => void;
};

const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

const QuestionMC: React.FC<Props> = ({ question, selected, onSelect }) => {
  const tableMc = getTableChoiceData(question.choices);
  if (tableMc) {
    return (
      <Box sx={{ mt: 2 }}>
        <McChoiceTableStudent
          columns={tableMc.columns}
          rows={tableMc.rows}
          selectedIndex={selected}
          onSelect={onSelect}
          renderCell={(html) => <RichText html={html} />}
        />
      </Box>
    );
  }

  const listChoices = choicesAsFlatStrings(question.choices);

  return (
    <Box sx={{ mt: 2 }}>
      {listChoices.map((choice, i) => {
        const isSelected = selected === i;
        const borderColor = isSelected ? 'primary.main' : 'divider';

        return (
          <Paper
            key={i}
            role="radio"
            aria-checked={isSelected}
            tabIndex={0}
            variant="outlined"
            onClick={() => onSelect(i)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(i);
              }
            }}
            sx={{
              p: 1.25,
              mb: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              cursor: 'pointer',
              borderColor,
              borderWidth: 1,
              bgcolor: isSelected ? 'action.hover' : 'background.paper',
              transition: 'background 120ms, border-color 120ms, border-width 120ms',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: isSelected ? 'action.hover' : 'action.selected',
              },
              outline: 'none',
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '999px',
                border: '1px solid',
                borderColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                flex: '0 0 auto',
                mt: 0.25,
                userSelect: 'none',
              }}
            >
              {letters[i] ?? String(i + 1)}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <RichText html={choice} />
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default QuestionMC;
